import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { getStoredRole, getTopNavLinks } from '../utils/navigation';

const C = {
  bg:      '#0A0E1A',
  surface: '#111827',
  surface2:'#161E2D',
  border:  '#1F2937',
  accent:  '#10B981',
  accentD: '#059669',
  warn:    '#F59E0B',
  danger:  '#EF4444',
  text:    '#F9FAFB',
  muted:   '#6B7280',
  input:   '#1C2433',
};

const s = {
  page: { minHeight: '100vh', background: C.bg, color: C.text, fontFamily: "'DM Sans', 'Segoe UI', sans-serif" },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 32px', borderBottom: `1px solid ${C.border}`, background: C.surface },
  logo: { fontSize: 20, fontWeight: 700, color: C.accent },
  nav: { display: 'flex', gap: 24 },
  navLink: (active) => ({ color: active ? C.accent : C.muted, textDecoration: 'none', fontSize: 14, fontWeight: active ? 700 : 400 }),
  layout: { display: 'grid', gridTemplateColumns: '220px 1fr', gap: 0, maxWidth: 1280, margin: '0 auto' },
  sidebar: { borderRight: `1px solid ${C.border}`, padding: '28px 0', minHeight: 'calc(100vh - 61px)' },
  sideItem: (active) => ({
    display: 'block', padding: '10px 24px', fontSize: 14, fontWeight: active ? 600 : 400,
    color: active ? C.accent : C.muted, cursor: 'pointer', borderLeft: active ? `3px solid ${C.accent}` : '3px solid transparent',
    background: active ? `${C.accent}11` : 'transparent', textDecoration: 'none',
  }),
  sideGroup: { fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.1em', padding: '20px 24px 6px' },
  content: { padding: '32px' },
  heading: { fontSize: 22, fontWeight: 700, marginBottom: 4, letterSpacing: '-0.5px' },
  sub: { fontSize: 13, color: C.muted, marginBottom: 28 },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '24px', marginBottom: 20 },
  cardTitle: { fontSize: 15, fontWeight: 700, marginBottom: 4 },
  cardDesc: { fontSize: 13, color: C.muted, marginBottom: 20 },
  field: { marginBottom: 18 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 7 },
  hint: { fontSize: 11, color: C.muted, marginTop: 5 },
  input: { width: '100%', background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 14px', color: C.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  inputFocus: { borderColor: C.accent },
  inputReadonly: { background: '#0D1320', color: C.muted, cursor: 'not-allowed' },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  btnPrimary: { background: C.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnDanger: { background: 'transparent', color: C.danger, border: `1px solid ${C.danger}55`, borderRadius: 8, padding: '10px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 22px', fontSize: 14, cursor: 'pointer' },
  toggle: (on) => ({
    width: 44, height: 24, borderRadius: 12, cursor: 'pointer', border: 'none',
    background: on ? C.accent : C.border, position: 'relative', transition: 'background 0.2s',
    flexShrink: 0,
  }),
  toggleThumb: (on) => ({
    position: 'absolute', top: 3, left: on ? 23 : 3, width: 18, height: 18,
    borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
  }),
  switchRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: `1px solid ${C.border}22` },
  switchLabel: { fontSize: 14, fontWeight: 600 },
  switchDesc: { fontSize: 12, color: C.muted },
  apiKeyBox: { background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 16px', fontFamily: 'monospace', fontSize: 13, color: C.accent, wordBreak: 'break-all', letterSpacing: '0.03em' },
  copyBtn: { background: 'transparent', border: `1px solid ${C.border}`, color: C.muted, borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer', marginTop: 10 },
  toast: (type) => ({
    position: 'fixed', top: 20, right: 20, padding: '12px 20px',
    borderRadius: 10, fontSize: 14, fontWeight: 600, zIndex: 2000,
    background: type === 'success' ? C.accent : C.danger, color: '#fff',
  }),
};

const TABS = [
  { id: 'perfil',      label: 'Perfil del comercio', icon: '🏪' },
  { id: 'api',         label: 'API & Webhooks',       icon: '🔌' },
  { id: 'notificaciones', label: 'Notificaciones',    icon: '🔔' },
  { id: 'seguridad',   label: 'Seguridad',            icon: '🔐' },
];

export default function Settings() {
  const userRole = getStoredRole();
  const topNavLinks = getTopNavLinks(userRole);
  const [tab, setTab]         = useState('perfil');
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState(null);
  const [copied, setCopied]   = useState(false);
  const [merchantId, setMerchantId] = useState(null);

  // Perfil
  const [perfil, setPerfil] = useState({
    business_name: '', email: '', phone: '', address: '', website: '', webhook_url: ''
  });

  // Notificaciones
  const [notif, setNotif] = useState({
    email_pago_exitoso: true,
    email_pago_fallido: true,
    email_liquidacion: true,
    push_pago_exitoso: false,
  });

  // Seguridad
  const [security, setSecurity] = useState({
    two_factor: false,
    ip_whitelist: '',
  });

  // API Keys (solo lectura)
  const [apiKeys, setApiKeys] = useState({ api_key: null, webhook_secret: null });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const r = await api.get('/merchants/me');
      const merchant = r?.data || {};
      setMerchantId(merchant.id || null);
      setPerfil({
        business_name: merchant.business_name || '',
        email: merchant.email || '',
        phone: merchant.phone || '',
        address: merchant.address || '',
        website: merchant.website || '',
        webhook_url: merchant.webhook_url || '',
      });
      setApiKeys({ api_key: merchant.api_key || null, webhook_secret: merchant.webhook_secret || null });
    } catch (e) {
      notify('❌ No se pudo cargar configuración', 'error');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/merchants/me', { ...perfil, merchant_id: merchantId });
      notify('✅ Cambios guardados', 'success');
    } catch {
      notify('❌ Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const notify = (text, type) => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3000);
  };

  const regenerateApiKeys = async () => {
    try {
      const r = await api.post('/merchants/me/regenerate-keys', { merchant_id: merchantId });
      setApiKeys({
        api_key: r?.data?.api_key || null,
        webhook_secret: r?.data?.webhook_secret || null,
      });
      notify('✅ API keys regeneradas', 'success');
    } catch {
      notify('❌ No se pudo regenerar API keys', 'error');
    }
  };

  const downloadMyData = () => {
    const data = {
      exported_at: new Date().toISOString(),
      perfil,
      notificaciones: notif,
      seguridad: security,
      api_keys: apiKeys,
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `mis-datos-fintechcoop-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    notify('✅ Datos descargados', 'success');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const Toggle = ({ value, onChange }) => (
    <button style={s.toggle(value)} onClick={() => onChange(!value)}>
      <div style={s.toggleThumb(value)} />
    </button>
  );

  return (
    <div style={s.page}>
      {msg && <div style={s.toast(msg.type)}>{msg.text}</div>}

      {/* Topbar */}
      <div style={s.topbar}>
        <div style={s.logo}>⚡ FintechCoop</div>
        <nav style={s.nav}>
          {topNavLinks.map(link => (
            <Link key={link.to} to={link.to} style={s.navLink(link.to === '/settings')}>
              {link.label}
            </Link>
          ))}
        </nav>
      </div>

      <div style={s.layout}>
        {/* Sidebar */}
        <aside style={s.sidebar}>
          <div style={s.sideGroup}>Configuración</div>
          {TABS.map(t => (
            <div key={t.id} style={s.sideItem(tab === t.id)} onClick={() => setTab(t.id)}>
              {t.icon} {t.label}
            </div>
          ))}
        </aside>

        {/* Contenido */}
        <div style={s.content}>

          {/* ── PERFIL ── */}
          {tab === 'perfil' && (
            <>
              <div style={s.heading}>Perfil del comercio</div>
              <div style={s.sub}>Información básica de tu negocio</div>

              <div style={s.card}>
                <div style={s.cardTitle}>Datos del negocio</div>
                <div style={s.cardDesc}>Esta información aparece en los recibos de tus clientes</div>

                <div style={s.row2}>
                  <Field label="Nombre del negocio" value={perfil.business_name}
                    onChange={v => setPerfil(p => ({ ...p, business_name: v }))} />
                  <Field label="Email de contacto" type="email" value={perfil.email}
                    onChange={v => setPerfil(p => ({ ...p, email: v }))} />
                </div>
                <div style={s.row2}>
                  <Field label="Teléfono" value={perfil.phone}
                    onChange={v => setPerfil(p => ({ ...p, phone: v }))} />
                  <Field label="Sitio web" value={perfil.website}
                    onChange={v => setPerfil(p => ({ ...p, website: v }))} />
                </div>
                <Field label="Dirección" value={perfil.address}
                  onChange={v => setPerfil(p => ({ ...p, address: v }))} />
              </div>

              <div style={s.card}>
                <div style={s.cardTitle}>Webhook URL</div>
                <div style={s.cardDesc}>URL donde recibirás notificaciones de pagos en tiempo real</div>
                <Field label="Endpoint" placeholder="https://tuapi.com/webhooks/pagos"
                  value={perfil.webhook_url} onChange={v => setPerfil(p => ({ ...p, webhook_url: v }))} />
                <div style={{ fontSize: 12, color: C.muted, marginTop: -10 }}>
                  Recibirás eventos: <code style={{ color: C.accent }}>payment.succeeded</code>, <code style={{ color: C.accent }}>payment.failed</code>, <code style={{ color: C.accent }}>payment.refunded</code>
                </div>
              </div>

              <button style={s.btnPrimary} onClick={save} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </>
          )}

          {/* ── API & WEBHOOKS ── */}
          {tab === 'api' && (
            <>
              <div style={s.heading}>API & Webhooks</div>
              <div style={s.sub}>Claves para integrar FintechCoop en tu sistema</div>

              <div style={s.card}>
                <div style={s.cardTitle}>API Key (pública)</div>
                <div style={s.cardDesc}>Usa esta clave para autenticar requests desde tu backend</div>
                {apiKeys.api_key && (
                  <>
                    <div style={s.apiKeyBox}>{apiKeys.api_key}</div>
                    <button style={s.copyBtn} onClick={() => copyToClipboard(apiKeys.api_key)}>
                      {copied ? '✅ Copiado' : '📋 Copiar'}
                    </button>
                  </>
                )}
              </div>

              <div style={s.card}>
                <div style={s.cardTitle}>Webhook Secret</div>
                <div style={s.cardDesc}>Usa este secreto para verificar la firma de los webhooks que recibes</div>
                {apiKeys.webhook_secret && (
                  <>
                    <div style={s.apiKeyBox}>{apiKeys.webhook_secret}</div>
                    <button style={s.copyBtn} onClick={() => copyToClipboard(apiKeys.webhook_secret)}>
                      {copied ? '✅ Copiado' : '📋 Copiar'}
                    </button>
                  </>
                )}
                <div style={{ fontSize: 12, color: C.muted, marginTop: 12 }}>
                  Verifica la firma con el header <code style={{ color: C.accent }}>X-Signature</code> usando HMAC-SHA256
                </div>
              </div>

              <div style={s.card}>
                <div style={s.cardTitle}>Ejemplo de verificación</div>
                <div style={{ background: C.surface2, borderRadius: 8, padding: '16px', fontFamily: 'monospace', fontSize: 12, color: '#A5B4FC', lineHeight: 1.7, overflowX: 'auto' }}>
                  <div style={{ color: C.muted }}># Python</div>
                  <div>import hmac, hashlib</div>
                  <div>sig = hmac.new(WEBHOOK_SECRET, body, hashlib.sha256).hexdigest()</div>
                  <div>assert hmac.compare_digest(sig, request.headers['X-Signature'])</div>
                </div>
              </div>
            </>
          )}

          {/* ── NOTIFICACIONES ── */}
          {tab === 'notificaciones' && (
            <>
              <div style={s.heading}>Notificaciones</div>
              <div style={s.sub}>Configura cuándo y cómo quieres recibir alertas</div>

              <div style={s.card}>
                <div style={s.cardTitle}>Email</div>
                <div style={s.cardDesc}>Notificaciones al email registrado</div>
                {[
                  { key: 'email_pago_exitoso', label: 'Pago exitoso',        desc: 'Cuando un cliente paga correctamente' },
                  { key: 'email_pago_fallido', label: 'Pago fallido',        desc: 'Cuando un pago es rechazado' },
                  { key: 'email_liquidacion',  label: 'Liquidación lista',   desc: 'Cuando se genera tu liquidación diaria' },
                ].map(({ key, label, desc }) => (
                  <div key={key} style={s.switchRow}>
                    <div>
                      <div style={s.switchLabel}>{label}</div>
                      <div style={s.switchDesc}>{desc}</div>
                    </div>
                    <Toggle value={notif[key]} onChange={v => setNotif(n => ({ ...n, [key]: v }))} />
                  </div>
                ))}
              </div>

              <div style={s.card}>
                <div style={s.cardTitle}>Push (web)</div>
                <div style={s.cardDesc}>Notificaciones en el navegador en tiempo real</div>
                <div style={s.switchRow}>
                  <div>
                    <div style={s.switchLabel}>Pago exitoso</div>
                    <div style={s.switchDesc}>Notificación instantánea al recibir un pago</div>
                  </div>
                  <Toggle value={notif.push_pago_exitoso} onChange={v => setNotif(n => ({ ...n, push_pago_exitoso: v }))} />
                </div>
              </div>

              <button style={s.btnPrimary} onClick={save} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar preferencias'}
              </button>
            </>
          )}

          {/* ── SEGURIDAD ── */}
          {tab === 'seguridad' && (
            <>
              <div style={s.heading}>Seguridad</div>
              <div style={s.sub}>Protege el acceso a tu cuenta de comercio</div>

              <div style={s.card}>
                <div style={s.cardTitle}>Autenticación en dos pasos (2FA)</div>
                <div style={s.cardDesc}>Requiere un código adicional al iniciar sesión</div>
                <div style={s.switchRow}>
                  <div>
                    <div style={s.switchLabel}>Activar 2FA</div>
                    <div style={s.switchDesc}>Usar app autenticadora (Google Authenticator, Authy)</div>
                  </div>
                  <Toggle value={security.two_factor} onChange={v => setSecurity(p => ({ ...p, two_factor: v }))} />
                </div>
                {security.two_factor && (
                  <div style={{ marginTop: 16, padding: 16, background: `${C.accent}11`, borderRadius: 8, fontSize: 13, color: C.accent }}>
                    ✅ 2FA activado — tu cuenta está protegida
                  </div>
                )}
              </div>

              <div style={s.card}>
                <div style={s.cardTitle}>Whitelist de IPs</div>
                <div style={s.cardDesc}>Solo estas IPs podrán hacer llamadas a la API (una por línea)</div>
                <textarea
                  style={{ ...s.input, height: 100, resize: 'vertical', marginTop: 0 }}
                  placeholder="192.168.1.1&#10;10.0.0.0/24"
                  value={security.ip_whitelist}
                  onChange={e => setSecurity(p => ({ ...p, ip_whitelist: e.target.value }))}
                />
                <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Deja en blanco para permitir todas las IPs</div>
              </div>

              <div style={s.card}>
                <div style={s.cardTitle}>Zona de peligro</div>
                <div style={s.cardDesc}>Acciones irreversibles — úsalas con precaución</div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button style={s.btnDanger} onClick={regenerateApiKeys}>
                    Regenerar API Key
                  </button>
                  <button style={s.btnSecondary} onClick={downloadMyData}>
                    Descargar mis datos
                  </button>
                </div>
              </div>

              <button style={s.btnPrimary} onClick={save} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar configuración'}
              </button>
            </>
          )}

        </div>
      </div>
    </div>
  );
}

/* ── Componente Field reutilizable ─────────────────────────── */
function Field({ label, value, onChange, type = 'text', placeholder = '', readonly = false }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 7 }}>{label}</label>
      <input
        type={type}
        style={{
          width: '100%',
          background: readonly ? '#0D1320' : '#1C2433',
          border: `1px solid #1F2937`,
          borderRadius: 8,
          padding: '10px 14px',
          color: readonly ? '#6B7280' : '#F9FAFB',
          fontSize: 14,
          outline: 'none',
          boxSizing: 'border-box',
          cursor: readonly ? 'not-allowed' : 'text',
        }}
        value={value || ''}
        onChange={readonly ? undefined : e => onChange(e.target.value)}
        placeholder={placeholder}
        readOnly={readonly}
      />
    </div>
  );
}
